import { BusinessObject } from './business-object.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
	jsonMapProperty,
} from '@sumbricht/json-class-serializer'
import { BusinessObjectWithDataFile } from './business-object-with-data-file.js'
import type { ResourceAvailabilityOnClientType } from '../types.js'

export const ResourceTypes = {
	'application/pdf': 'PDF document',
	'text/plain': 'Plain text',
	'text/css': '(S)CSS style sheet',
	'text/html': 'HTML document',
	'image/png': 'PNG image',
	'image/jpeg': 'JPG image',
	'image/gif': 'GIF image',
	'image/svg+xml': 'SVG image',
	'image/bmp': 'BMP image',
	'image/tiff': 'TIFF image',
	'application/zip': 'ZIP archive',
	'text/csv': 'CSV file',
	'application/vnd.ms-excel': 'MS Excel workbook',
	'application/msword': 'MS Word document',
	'text/xml': 'XML document',
	'video/mp4': 'MP4 video',
	'application/octet-stream': 'Other binary file',
} as const
export type ResourceType = keyof typeof ResourceTypes
export const ResourceAvailabilityOnClientTypes: Record<
	ResourceAvailabilityOnClientType,
	string
> = {
	none: 'None (only usable on server-side)',
	downloadable: 'Can be downloaded (e.g. URL, file name are exposed)',
	builtin:
		'Built into client app (use only when absolutely necessary as it increases the app size!)',
}

@jsonClass('Resource')
export class Resource
	extends BusinessObject
	implements BusinessObjectWithDataFile
{
	@jsonProperty(String) dataBase64: string = ''
	@jsonProperty(String) resourceType?: ResourceType
	@jsonProperty(String) overrideMimeType: string = ''
	@jsonProperty(String) fileName: string = ''
	@jsonProperty(String) availabilityOnClient: ResourceAvailabilityOnClientType =
		'none'

	get textData() {
		return atob(this.dataBase64)
	}
	set textData(text: string) {
		this.dataBase64 = btoa(text)
	}

	constructor(init?: Partial<Resource>) {
		super()
		assignCommonProperties(this, init)
	}

	hasDataFile(): this is BusinessObjectWithDataFile {
		return true
	}

	getEffectiveMimeType() {
		return (this.overrideMimeType || this.resourceType) ?? ''
	}

	getClassNames(): string[] {
		return ['$Resources']
	}

	isDownloadable() {
		return (
			this.availabilityOnClient == 'downloadable' ||
			this.availabilityOnClient == 'builtin'
		)
	}
}
